import type { KnowledgeBaseItem } from "@/services/knowledgeBaseService";

export type GoogleDriveKbMetadata = {
  fileId: string;
  folderId?: string;
  modifiedTime: string;
  mimeType: string;
  url?: string;
  importedAt: string;
  drivePath?: string;
};

export type DriveSyncStatus = "new" | "synced" | "stale";

export function getDriveMetadata(
  kbItem: KnowledgeBaseItem
): GoogleDriveKbMetadata | null {
  let meta = kbItem.metadata;
  if (typeof meta === "string") {
    try {
      meta = JSON.parse(meta);
    } catch {
      return null;
    }
  }
  const drive = meta?.googleDrive;
  if (!drive || typeof drive.fileId !== "string") return null;
  return drive as GoogleDriveKbMetadata;
}

export function withDriveMetadata(
  existingMetadata: Record<string, unknown> | undefined | null,
  drive: GoogleDriveKbMetadata
): Record<string, unknown> {
  const base =
    existingMetadata && typeof existingMetadata === "object"
      ? { ...existingMetadata }
      : {};
  return { ...base, googleDrive: drive };
}

export function findKbDocByDriveFileId(
  kbItems: KnowledgeBaseItem[],
  fileId: string
): KnowledgeBaseItem | undefined {
  return kbItems.find((item) => getDriveMetadata(item)?.fileId === fileId);
}

export function isDriveFileStale(
  driveModifiedTime: string,
  kbMetadata: GoogleDriveKbMetadata | null
): boolean {
  if (!kbMetadata?.modifiedTime) return false;
  const driveTs = Date.parse(driveModifiedTime);
  const storedTs = Date.parse(kbMetadata.modifiedTime);
  if (Number.isNaN(driveTs) || Number.isNaN(storedTs)) return false;
  return driveTs > storedTs;
}

export function getDriveSyncStatus(
  fileId: string,
  driveModifiedTime: string,
  kbItems: KnowledgeBaseItem[]
): { status: DriveSyncStatus; kbDocId?: string } {
  const kbDoc = findKbDocByDriveFileId(kbItems, fileId);
  if (!kbDoc) return { status: "new" };
  const meta = getDriveMetadata(kbDoc);
  if (!meta) return { status: "new", kbDocId: kbDoc.id };
  if (isDriveFileStale(driveModifiedTime, meta)) {
    return { status: "stale", kbDocId: kbDoc.id };
  }
  return { status: "synced", kbDocId: kbDoc.id };
}
